import { useCallback, useEffect, useRef, useState } from "react";
import { images } from "../data/scenes";

export default function ImagesViewer() {
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);

  const touchStartX = useRef(null);

  /*
   * ---------------------------------------------------------
   * PRELOAD IMAGE
   * ---------------------------------------------------------
   */
  const preloadImage = useCallback((src) => {
    if (!src) return;

    const img = new Image();
    img.src = src;

    if (img.decode) {
      img.decode().catch(() => { });
    }
  }, []);

  /*
   * ---------------------------------------------------------
   * PRELOAD NEXT + PREVIOUS IMAGE
   * ---------------------------------------------------------
   *
   * IMPORTANT:
   * We do NOT preload all 23 MB images.
   * Only adjacent images are preloaded.
   */
  useEffect(() => {
    if (!images.length) return;

    const nextIndex =
      (index + 1) % images.length;

    const previousIndex =
      index === 0
        ? images.length - 1
        : index - 1;

    const timer = setTimeout(() => {
      preloadImage(images[nextIndex]);
      preloadImage(images[previousIndex]);
    }, 300);

    return () => clearTimeout(timer);
  }, [index, preloadImage]);

  /*
   * ---------------------------------------------------------
   * NEXT
   * ---------------------------------------------------------
   */
  const next = useCallback(() => {
    if (!images.length || transitioning) return;

    setTransitioning(true);
    setLoading(true);

    setIndex((current) =>
      (current + 1) % images.length
    );
  }, [transitioning]);

  /*
   * ---------------------------------------------------------
   * PREVIOUS
   * ---------------------------------------------------------
   */
  const prev = useCallback(() => {
    if (!images.length || transitioning) return;

    setTransitioning(true);
    setLoading(true);

    setIndex((current) =>
      current === 0
        ? images.length - 1
        : current - 1
    );
  }, [transitioning]);

  /*
   * ---------------------------------------------------------
   * IMAGE LOADED
   * ---------------------------------------------------------
   */
  const handleImageLoad = async (event) => {
    try {
      if (event.currentTarget.decode) {
        await event.currentTarget.decode();
      }
    } catch {
      // Ignore decode errors
    }

    setLoading(false);

    setTimeout(() => {
      setTransitioning(false);
    }, 300);
  };

  /*
   * ---------------------------------------------------------
   * KEYBOARD
   * ---------------------------------------------------------
   */
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowRight") {
        next();
      }

      if (event.key === "ArrowLeft") {
        prev();
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [next, prev]);

  /*
   * ---------------------------------------------------------
   * SWIPE
   * ---------------------------------------------------------
   */
  const handleTouchStart = (event) => {
    touchStartX.current =
      event.touches[0].clientX;
  };

  const handleTouchEnd = (event) => {
    if (touchStartX.current === null) {
      return;
    }

    const touchEndX =
      event.changedTouches[0].clientX;

    const difference =
      touchStartX.current - touchEndX;

    const threshold = 50;

    if (Math.abs(difference) > threshold) {
      if (difference > 0) {
        next();
      } else {
        prev();
      }
    }

    touchStartX.current = null;
  };

  /*
   * ---------------------------------------------------------
   * EMPTY ARRAY
   * ---------------------------------------------------------
   */
  if (!images.length) {
    return null;
  }

  const currentImage = images[index];

  return (
    <div
      style={styles.viewer}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* ================================================= */}
      {/* IMAGE */}
      {/* ================================================= */}

      <img
        key={index}
        src={currentImage}
        alt={`Atharva Landmark ${index + 1}`}
        draggable={false}
        decoding="async"
        fetchPriority="high"
        onLoad={handleImageLoad}
        style={{
          ...styles.image,

          opacity: loading ? 0 : 1,

          transform: loading
            ? "scale(1.025)"
            : "scale(1)",
        }}
      />

      {/* ================================================= */}
      {/* LOADING */}
      {/* ================================================= */}

      {loading && (
        <div style={styles.loading}>
          <div style={styles.loader} />

          <div style={styles.loadingText}>
            Loading...
          </div>
        </div>
      )}


      {/* ================================================= */}
      {/* BOTTOM CONTROLS */}
      {/* ================================================= */}

      <div style={styles.bottomBar}>
        <button
          type="button"
          onClick={prev}
          disabled={transitioning}
          style={styles.smallButton}
          aria-label="Previous image"
        >
          ←
        </button>

        <div style={styles.position}>
          {index + 1} / {images.length}
        </div>

        <button
          type="button"
          onClick={next}
          disabled={transitioning}
          style={styles.smallButton}
          aria-label="Next image"
        >
          →
        </button>
      </div>

      {/* ================================================= */}
      {/* MOBILE HINT */}
      {/* ================================================= */}

      <div style={styles.swipeHint}>
        Swipe or use ← →
      </div>

      {/* ================================================= */}
      {/* ANIMATION */}
      {/* ================================================= */}

      <style>
        {`
                    @keyframes photoAlbumSpin {
                        from {
                            transform: rotate(0deg);
                        }

                        to {
                            transform: rotate(360deg);
                        }
                    }

                    .photo-album-nav:hover {
                        background: rgba(37, 99, 235, 0.75) !important;
                        border-color: rgba(96, 165, 250, 0.8) !important;
                        transform: translateY(-50%) scale(1.08) !important;
                    }

                    .photo-album-small:hover {
                        background: rgba(37, 99, 235, 0.7) !important;
                    }
                `}
      </style>
    </div>
  );
}


/* ========================================================= */
/* STYLES */
/* ========================================================= */

const styles = {
  viewer: {
    width: "100%",
    height: "100dvh",

    position: "relative",

    overflow: "hidden",

    background: "#020617",

    color: "#fff",

    userSelect: "none",

    touchAction: "pan-y",

    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },

  image: {
    position: "absolute",

    inset: 0,

    width: "100%",
    height: "100%",

    objectFit: "contain",

    transition:
      "opacity 0.35s ease, transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)",

    userSelect: "none",

    WebkitUserDrag: "none",
  },

  loading: {
    position: "absolute",

    inset: 0,

    display: "flex",
    flexDirection: "column",

    alignItems: "center",
    justifyContent: "center",

    gap: 16,

    background:
      "radial-gradient(circle, rgba(15,23,42,0.65), rgba(0,0,0,0.9))",

    zIndex: 20,

    pointerEvents: "none",
  },

  loader: {
    width: 42,
    height: 42,

    borderRadius: "50%",

    border:
      "3px solid rgba(255,255,255,0.12)",

    borderTop:
      "3px solid #60a5fa",

    animation:
      "photoAlbumSpin 0.8s linear infinite",
  },

  loadingText: {
    fontSize: 13,

    color:
      "rgba(255,255,255,0.6)",

    letterSpacing: "0.5px",
  },

  topBar: {
    position: "absolute",

    top: 20,
    left: 25,
    right: 25,

    display: "flex",

    justifyContent: "space-between",
    alignItems: "center",

    zIndex: 30,

    pointerEvents: "none",
  },

  logo: {
    fontSize: 13,

    fontWeight: 700,

    letterSpacing: "3px",

    color: "#fff",

    textShadow:
      "0 2px 10px rgba(0,0,0,0.8)",
  },

  logoAccent: {
    marginLeft: 8,

    color: "#60a5fa",
  },

  counter: {
    display: "flex",

    alignItems: "center",

    gap: 8,

    padding: "8px 13px",

    borderRadius: 20,

    background:
      "rgba(0,0,0,0.45)",

    backdropFilter: "blur(10px)",

    color: "#fff",

    fontSize: 12,

    fontWeight: 600,
  },

  navButton: {
    position: "absolute",

    top: "50%",

    transform: "translateY(-50%)",

    width: 54,
    height: 54,

    display: "flex",

    alignItems: "center",
    justifyContent: "center",

    borderRadius: "50%",

    border:
      "1px solid rgba(255,255,255,0.18)",

    background:
      "rgba(2,6,23,0.55)",

    backdropFilter: "blur(12px)",

    color: "#fff",

    fontSize: 38,

    fontWeight: 200,

    lineHeight: 1,

    cursor: "pointer",

    zIndex: 30,

    transition:
      "all 0.25s ease",

    boxShadow:
      "0 8px 30px rgba(0,0,0,0.35)",
  },

  bottomBar: {
    position: "absolute",

    bottom: 25,

    left: "50%",

    transform: "translateX(-50%)",

    display: "flex",

    alignItems: "center",

    gap: 15,

    padding: "8px 12px",

    borderRadius: 30,

    background:
      "rgba(2,6,23,0.65)",

    backdropFilter: "blur(14px)",

    border:
      "1px solid rgba(255,255,255,0.1)",

    zIndex: 30,

    boxShadow:
      "0 10px 35px rgba(0,0,0,0.35)",
  },

  smallButton: {
    width: 38,
    height: 38,

    borderRadius: "50%",

    border:
      "1px solid rgba(255,255,255,0.15)",

    background:
      "rgba(255,255,255,0.07)",

    color: "#fff",

    fontSize: 18,

    cursor: "pointer",

    transition:
      "all 0.2s ease",
  },

  position: {
    minWidth: 50,

    textAlign: "center",

    fontSize: 12,

    color:
      "rgba(255,255,255,0.75)",
  },

  swipeHint: {
    position: "absolute",

    bottom: 18,

    right: 25,

    color:
      "rgba(255,255,255,0.3)",

    fontSize: 10,

    zIndex: 20,
  },
};